import type { AbsenceCalendarItem } from './absenceCalendarModel';

export type AbsenceHistoryStatus = '' | 'PENDING' | 'CONFIRMED' | 'DRAFT' | 'REJECTED' | 'CANCELLED';

type AbsenceHistoryFilter = {
  from: string;
  to: string;
  status: AbsenceHistoryStatus;
};

function isValidDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) return false;
  const parsed = new Date(0);
  parsed.setUTCFullYear(year, month - 1, day);
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
}

export function absenceHistoryRangeError(from: string, to: string): string | undefined {
  if (from && !isValidDate(from)) return '시작일을 올바른 날짜로 입력해 주세요.';
  if (to && !isValidDate(to)) return '종료일을 올바른 날짜로 입력해 주세요.';
  if (from && to && from > to) return '종료일은 시작일 이후로 입력해 주세요.';
  return undefined;
}

function recordedTime(item: AbsenceCalendarItem): number {
  const value = item.kind === 'request' ? item.request?.createdAt : item.entry?.source?.recordedAt;
  const timestamp = value ? Date.parse(value) : Number.NaN;
  if (Number.isFinite(timestamp)) return timestamp;
  return isValidDate(item.startDate) ? Date.parse(item.startDate) : 0;
}

export function sortAbsenceHistory(items: readonly AbsenceCalendarItem[]): AbsenceCalendarItem[] {
  return [...items].sort((left, right) => {
    const byTime = recordedTime(right) - recordedTime(left);
    if (byTime) return byTime;
    const leftKey = `${left.id}:${left.kind}`;
    const rightKey = `${right.id}:${right.kind}`;
    return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
  });
}

function matchesStatus(item: AbsenceCalendarItem, status: AbsenceHistoryStatus): boolean {
  if (!status) return true;
  const value = item.kind === 'request' ? item.request?.status : item.entry?.source?.status;
  switch (status) {
    case 'PENDING': return (item.kind === 'request' && value === 'SUBMITTED') || item.request?.cancellationStatus === 'SUBMITTED';
    case 'CONFIRMED': return item.kind === 'request' ? value === 'APPROVED' : value === 'CONFIRMED';
    case 'REJECTED': return value === 'REJECTED' || value === 'DENIED';
    default: return value === status;
  }
}

export function filterAbsenceHistory(items: readonly AbsenceCalendarItem[], filter: AbsenceHistoryFilter): AbsenceCalendarItem[] {
  if (absenceHistoryRangeError(filter.from, filter.to)) return [];
  return sortAbsenceHistory(items.filter((item) =>
    (!filter.from || item.endDate >= filter.from)
    && (!filter.to || item.startDate <= filter.to)
    && matchesStatus(item, filter.status),
  ));
}
